import { useContextSelector } from "use-context-selector";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { isEmpty, lowerCase, startCase } from "lodash";
import jwt_decode from "jwt-decode";
import { toast } from "react-toastify";
import { t } from "i18next";
import { isMobile } from "react-device-detect";

import { apiGet } from "../../Utils/apiFetch";
import apiPath from "../../Utils/apiPath";
import { cdnImg } from "../../Utils/cdnImage";

import AuthContext from "../../context/AuthContext";
import BetSlipContext from "../../context/BetSlipContext";
import ThemeContext from "../../context/ThemeContext";

import Layout from "../Layout";
import SportsHeader from "./SportsHeader";
import ListingSports from "./ListingSports";
import ListingSportComp from "./ListingSportComp";
import Leagues from "./Leagues";
import NoEvent from "../../component/NoEvent";
import Search from "./Search";
import Notifications from "../../component/Notifications";
import BetSlip from "../matchDetail/BetSlip";
import BeforeLoader from "./BeforeLoader";
import BetSlipIcon from "../../component/BetSlipIcon";
import MyBets from "../../component/MyBets";
import BetSlipParly from "../../component/BetSlipParly";
import CustomToast from "../../Utils/CustomToast";
import Setting from "./Setting";
import MyFav from "./MyFav";
import PageWrapper from "../../component/PageWrapper";
import { Favourite } from "../casino/ListHome";
import SortDropdown from "./SortDropdown";
import FilterTabs from "./FilterTabs";
import { useStablePolling } from "./useStablePolling";

const mergeSeries = (array) => {
  if (!array?.length) return [];
  const others = array.filter((s) => s.seriesName === "Others");
  if (others.length <= 1) return array;
  const base = others[0];
  return [
    ...array.filter((s) => s.seriesName !== "Others"),
    { ...base, matchList: others.flatMap((s) => s.matchList) },
  ];
};

const sortByMatched = (data) =>
  [...data]
    .map((series) => ({
      ...series,
      matchList: [...series.matchList].sort(
        (a, b) => parseFloat(b.totalMatched) - parseFloat(a.totalMatched),
      ),
    }))
    .sort((a, b) => {
      const max = (s) =>
        Math.max(...s.matchList.map((m) => parseFloat(m.totalMatched)));
      return max(b) - max(a);
    });

const SPORTS = ["cricket", "soccer", "tennis"];

const EMPTY_ALL = { cricket: [], soccer: [], tennis: [] };
const EMPTY_SHOW_ALL = { cricket: false, soccer: false, tennis: false };

const Listing = () => {
  const parlay = useContextSelector(AuthContext, (c) => c.parlay);
  const user = useContextSelector(AuthContext, (c) => c.user);
  const theme = useContextSelector(ThemeContext, (c) => c.theme);
  const sportTab = useContextSelector(AuthContext, (c) => c.sportTab);
  const announcement = useContextSelector(AuthContext, (c) => c.announcement);
  const tab = useContextSelector(AuthContext, (c) => c.tab);
  const setTab = useContextSelector(AuthContext, (c) => c.setTab);
  const launchCasino = useContextSelector(AuthContext, (c) => c.launchCasino);
  const setCasinoBefore = useContextSelector(
    AuthContext,
    (c) => c.setCasinoBefore,
  );
  const setToastObject = useContextSelector(
    BetSlipContext,
    (c) => c.setToastObject,
  );
  const toastObject = useContextSelector(BetSlipContext, (c) => c.toastObject);

  // ─── local state ──────────────────────────────────────────────────────
  const [setting, setSetting] = useState(false);
  const [search, setSearch] = useState(false);
  const [loader, setLoader] = useState(false);
  const [selectedOption, setSelectedOption] = useState("by Competition");

  // flat match arrays (used for "by Time" view and odds fetching)
  const [flatData, setFlatData] = useState([]); // was `data` for non-all tabs
  const [flatDataAll, setFlatDataAll] = useState(EMPTY_ALL); // was `data` for all tab

  // series/competition arrays (used for "by Competition"/"by Matched" view)
  const [compData, setCompData] = useState([]);
  const [compDataAll, setCompDataAll] = useState(EMPTY_ALL);

  // odds
  const [allOdds, setAllOdds] = useState([]);
  const [multiOddsPremium, setMultiOddsPremium] = useState([]);

  // event id lists used to drive polling
  const [matchOddsTargets, setMatchOddsTargets] = useState([]); // [{marketId, eventId}]
  const [premiumTargets, setPremiumTargets] = useState([]); // [eventId, ...]

  // UI state
  const [count, setCount] = useState({
    cricketInplayCount: 0,
    soccerInplayCount: 0,
    tennisInplayCount: 0,
  });
  const [list, setList] = useState([]);
  const [activeFav, setActiveFav] = useState([]);
  const [showScore, setShowScore] = useState([]);
  const [showAll, setShowAll] = useState(EMPTY_SHOW_ALL);
  const [activeKeysAll, setActiveKeysAll] = useState(EMPTY_ALL);

  const isFirstRun = useRef(true);
  const oddsAbortRef = useRef(null);

  const isSportTab = sportTab;
  const stableTab = tab;
  const location = useLocation();

  const getOdds = useCallback(async (targets, signal) => {
    if (!targets?.length) return;
    const marketIds = targets.map((r) => r.marketId).join(",");
    const eventIds = targets.map((r) => r.eventId).join(",");
    const { data } = await apiGet(
      `${apiPath.getMatchOdds}?marketId=${marketIds}&multi=true&eventIds=${eventIds}`,
      {},
      signal,
    );
    if (signal?.aborted) return;
    if (data?.success && data.results?.length > 0) setAllOdds(data.results);
  }, []);

  const getPremiumMulti = useCallback(async (ids, signal) => {
    if (!ids?.length) return;
    const { data } = await apiGet(
      `${apiPath.getPremiumFancy}?events=${[...new Set(ids)].join(",")}&parly=true&multi=true`,
      {},
      signal,
    );
    if (signal?.aborted) return;
    if (data?.success) setMultiOddsPremium(data.results);
  }, []);

  const inPlayCount = useCallback(async () => {
    const { data } = await apiGet(apiPath.inPlayCountMobile, {
      type: stableTab,
    });
    if (data?.success && data.results) setCount(data.results);
  }, [stableTab]);

  const leagueData = useCallback(async () => {
    const { data } = await apiGet(apiPath.seriesList, { gameType: isSportTab });
    if (data?.success) setList(data.results);
  }, [isSportTab]);

  const getData = useCallback(
    async ({ manageLoader = true } = {}) => {
      oddsAbortRef.current?.abort();
      oddsAbortRef.current = new AbortController();

      setAllOdds([]);
      setMultiOddsPremium([]);
      setMatchOddsTargets([]);
      setPremiumTargets([]);

      if (manageLoader) setLoader(true);

      const userId = !isEmpty(localStorage.getItem("token"))
        ? jwt_decode(localStorage.getItem("token"))?.user?._id
        : "";

      const { data } = await apiGet(apiPath.seriesList, {
        gameType: isSportTab,
        type: stableTab,
        userId,
      });

      if (data?.success) {
        const seriesData = data.results ?? [];
        const allMatches = seriesData.flatMap((s) => s.matchList ?? []);

        // favourites
        setActiveFav(
          allMatches.filter((m) => m.isFavourite).map((m) => m.eventId),
        );

        if (parlay) {
          setPremiumTargets(allMatches.map((m) => m.eventId));
        } else {
          setMatchOddsTargets(
            allMatches.filter((m) => m.hasMatchOdds !== false),
          );
        }

        if (isSportTab === "all") {
          const shaped = Object.fromEntries(
            SPORTS.map((sport) => {
              const merged = mergeSeries(
                seriesData.filter((s) => s.gameType === sport),
              );
              return [sport, merged];
            }),
          );

          setCompDataAll(shaped);

          setFlatDataAll(
            Object.fromEntries(
              SPORTS.map((sport) => [
                sport,
                shaped[sport].flatMap((s) => s.matchList),
              ]),
            ),
          );

          setActiveKeysAll(
            Object.fromEntries(
              SPORTS.map((sport) => [
                sport,
                shaped[sport]
                  .filter((s) => s.hasMatchOddsInList)
                  .map((s) => s.seriesId),
              ]),
            ),
          );
        } else {
          const merged = mergeSeries(seriesData);

          setCompData(merged);
          setFlatData(merged.flatMap((s) => s.matchList ?? []));
          setActiveKeysAll({
            [isSportTab]: seriesData
              .filter((s) => s.hasMatchOddsInList !== false)
              .map((s) => s.seriesId),
          });
        }
      }

      if (manageLoader) setLoader(false);
    },
    [isSportTab, stableTab, parlay],
  );

  const fetchOdds = useCallback(async () => {
    if (!matchOddsTargets?.length || parlay) return;
    if (!["by Competition", "by Matched"].includes(selectedOption)) return;
    const signal = oddsAbortRef.current?.signal;
    await getOdds(matchOddsTargets, signal);
  }, [matchOddsTargets, parlay, selectedOption, getOdds]);

  const fetchPremium = useCallback(async () => {
    if (!premiumTargets?.length || isEmpty(user) || !parlay) return;
    if (!["by Competition", "by Matched"].includes(selectedOption)) return;
    const signal = oddsAbortRef.current?.signal;
    await getPremiumMulti(premiumTargets, signal);
  }, [premiumTargets, user, parlay, selectedOption, getPremiumMulti]);

  const oddsEnabled =
    matchOddsTargets.length > 0 &&
    !parlay &&
    ["by Competition", "by Matched"].includes(selectedOption);

  const premiumEnabled =
    premiumTargets.length > 0 &&
    !isEmpty(user) &&
    parlay &&
    ["by Competition", "by Matched"].includes(selectedOption);

  useStablePolling(fetchOdds, 5000, oddsEnabled);
  useStablePolling(fetchPremium, 10000, premiumEnabled);

  useEffect(() => {
    inPlayCount();
    getData();
  }, []);

  // Refresh listing every 10s without the loader; restarts when tab/sport/parlay changes
  const getDataRef = useRef(getData);
  useEffect(() => {
    getDataRef.current = getData;
  }, [getData]);

  useEffect(() => {
    if (stableTab === "leagues" || isSportTab === "myfav") return;
    const id = setInterval(
      () => getDataRef.current({ manageLoader: false }),
      10000,
    );
    return () => clearInterval(id);
  }, [stableTab, isSportTab, parlay]);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    if (stableTab === "leagues") {
      leagueData();
    } else if (isSportTab !== "myfav") {
      inPlayCount();
      getData();
    }
  }, [stableTab, isSportTab, parlay]);

  useEffect(() => () => oddsAbortRef.current?.abort(), []);

  const handleCasinoLaunch = useCallback(
    (res) => {
      if (isEmpty(user)) {
        setCasinoBefore(true);
        return;
      }
      if (isEmpty(res?.platFormCode)) {
        toast.error(t("ComingSoon"));
        return;
      }
      launchCasino({
        platForm: res.platFormCode,
        gameType: res.gameType,
        casinoType: res.casinoType,
        isLobbyTrue: res.isLobbyTrue,
      });
    },
    [user, launchCasino, setCasinoBefore],
  );

  const handleScore = useCallback((id) => {
    setShowScore((prev) =>
      prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id],
    );
  }, []);

  const handleActive = useCallback(
    (key, id, check) => {
      if (isEmpty(id)) {
        setActiveKeysAll((prev) => ({
          ...prev,
          [key]: check
            ? (isSportTab === "all" ? compDataAll[key] : compData).map(
                (s) => s.seriesId,
              )
            : [],
        }));
        setShowAll((prev) => ({ ...prev, [key]: check }));
      } else {
        setActiveKeysAll((prev) => ({
          ...prev,
          [key]: prev[key]?.includes(id)
            ? prev[key].filter((s) => s !== id)
            : [...prev[key], id],
        }));
      }
    },
    [isSportTab, compDataAll, compData],
  );

  const addToFavourite = useCallback(
    async (id, favoriteType, favoriteMarketId, eventType) => {
      if (isEmpty(user)) {
        setToastObject({
          type: "warning",
          title: "Warning",
          message: "Please login to add to favourite!",
          status: true,
        });
        return;
      }
      const { data } = await apiGet(
        `${apiPath.addToFavourite}?eventId=${id}&favoriteType=${favoriteType}&favoriteMarketId=${favoriteMarketId}&eventType=${eventType}`,
      );
      if (data?.success) {
        setActiveFav((prev) =>
          prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
        );
      }
    },
    [user, setToastObject],
  );

  const odds = parlay ? multiOddsPremium : allOdds;

  const isAllTab = isSportTab === "all";
  const isByTime = selectedOption === "by Time";
  const isByComp = !isByTime;

  const resolvedCompData = useMemo(() => {
    if (isAllTab) return compDataAll;
    if (selectedOption === "by Matched") return sortByMatched(compData);
    return compData;
  }, [isAllTab, compData, compDataAll, selectedOption]);

  const resolvedFlatData = useMemo(() => {
    if (isAllTab) return flatDataAll;
    return flatData;
  }, [isAllTab, flatData, flatDataAll]);

  const sharedListProps = {
    handleScore,
    showScore,
    addToFavioute: addToFavourite,
    activeFav,
  };

  const renderByTime = () => {
    if (isAllTab) {
      const hasData = SPORTS.some((s) => resolvedFlatData[s]?.length > 0);
      if (!hasData) return <NoEvent />;
      return SPORTS.filter((s) => resolvedFlatData[s]?.length > 0).map((s) => (
        <ListingSports
          key={s}
          heading={startCase(s)}
          data={resolvedFlatData[s]}
          isSportTab={isSportTab}
          {...sharedListProps}
        />
      ));
    }
    return resolvedFlatData?.length > 0 ? (
      <ListingSports
        heading={startCase(isSportTab)}
        data={resolvedFlatData}
        isSportTab={isSportTab}
        {...sharedListProps}
      />
    ) : (
      <NoEvent />
    );
  };

  const renderByComp = () => {
    if (isAllTab) {
      const hasData = SPORTS.some((s) => resolvedCompData[s]?.length > 0);
      if (!hasData) return <NoEvent />;
      return SPORTS.filter((s) => resolvedCompData[s]?.length > 0).map((s) => (
        <ListingSportComp
          key={s}
          heading={startCase(s)}
          data={resolvedCompData[s]}
          odds={odds}
          type={isSportTab}
          handle={handleActive}
          active={activeKeysAll[s]}
          showAll={showAll[s]}
          {...sharedListProps}
        />
      ));
    }
    return resolvedCompData?.length > 0 ? (
      <ListingSportComp
        heading={startCase(isSportTab)}
        data={resolvedCompData}
        odds={odds}
        type={isSportTab}
        handle={handleActive}
        active={activeKeysAll[lowerCase(isSportTab)]}
        showAll={showAll[lowerCase(isSportTab)]}
        {...sharedListProps}
      />
    ) : (
      <NoEvent />
    );
  };

  return (
    <Layout>
      <div id="vueWebComponent" className="cricket-container">
        <s-app data-v-app>
          <div
            id="s-app-container"
            className={`s-app s-space-y-px ${isMobile ? "s-default" : "is-desktop"}`}
          >
            <div className="s-main-container-wrapper">
              <section
                id="s-main-container"
                className="s-min-h-full-with-header s-mx-auto s-bg-body"
              >
                <main>
                  <div
                    style={
                      location.pathname.split("/")[1] === "sports"
                        ? { "--39a68b0e": "0vw" }
                        : { "--0f42db01": "0vw" }
                    }
                  >
                    <SportsHeader
                      setSetting={setSetting}
                      count={count}
                      setSearch={setSearch}
                    />
                    <PageWrapper>
                      {loader ? (
                        <BeforeLoader />
                      ) : isSportTab === "myfav" ? (
                        <MyFav />
                      ) : (
                        <>
                          {stableTab !== "leagues" && (
                            <div style={{ paddingTop: 0 }}>
                              <div className="s-px-2 s-py-10px s-flex s-justify-between">
                                <div className="s-flex s-gap-3px">
                                  <SortDropdown
                                    selectedOption={selectedOption}
                                    onSelect={setSelectedOption}
                                  />
                                  {["active", "today"].includes(stableTab) && (
                                    <FilterTabs
                                      activeTab={stableTab}
                                      theme={theme}
                                      onSelect={setTab}
                                    />
                                  )}
                                </div>

                                {isSportTab !== "all" &&
                                  selectedOption !== "by Time" && (
                                    <div
                                      onClick={() =>
                                        handleActive(
                                          isSportTab,
                                          "",
                                          !showAll[lowerCase(isSportTab)],
                                        )
                                      }
                                      className="s-flex s-gap-3px s-justify-center s-items-center s-p-7px s-rounded-5px s-cursor-pointer s-bg-filter-background-light dark:s-bg-filter-background-dark s-text-global-font-default-light dark:s-text-global-font-default-dark"
                                    >
                                      <span className="s-text-14px">
                                        Collapse
                                      </span>
                                      <span
                                        className={`s-icon-arrow-double s-text-11px s-transition-transform s-origin-center s-duration-300 s-text-sort-by-icon s-transform ${showAll[lowerCase(isSportTab)] ? "s-rotate-180" : ""}`}
                                      />
                                    </div>
                                  )}
                              </div>
                            </div>
                          )}

                          <div className="s-min-h-full-with-header">
                            {stableTab === "leagues" ? (
                              <Leagues data={list} />
                            ) : isByTime ? (
                              renderByTime()
                            ) : (
                              renderByComp()
                            )}
                          </div>
                        </>
                      )}
                      <BetSlipIcon />
                    </PageWrapper>
                  </div>

                  <div className="recommend ng-star-inserted">
                    <div className="recommend-title">
                      <h2>Favourite List</h2>
                    </div>
                    <div className="recommend-bg games">
                      <div className="recommend-main games-main">
                        {Favourite?.map((res) => (
                          <div
                            key={res.name}
                            onClick={() => handleCasinoLaunch(res)}
                            className="games-box ng-star-inserted"
                          >
                            <div className="pic">
                              <a helf="#">
                                <img
                                  alt={res.name}
                                  src={cdnImg(res.src)}
                                  loading="lazy"
                                />
                              </a>
                            </div>
                            <div className="text">
                              <h3>{res.name}</h3>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </main>
              </section>

              <BetSlipParly />
              <MyBets />
              <BetSlip />
              {setting && <Setting setSetting={setSetting} />}
              {search && <Search setSearch={setSearch} />}
              {announcement && <Notifications />}
              {toastObject?.status && (
                <CustomToast
                  type={toastObject.type}
                  title={toastObject.title}
                  message={toastObject.message}
                  onClose={() => setToastObject({})}
                  playType={toastObject.playType}
                />
              )}
            </div>
          </div>
        </s-app>
      </div>
    </Layout>
  );
};

export default Listing;
